type EnvironmentGroup = "fp" | "ncdex";

type Environment =
  | "fp-local"
  | "fp-sandbox"
  | "fp-staging"
  | "fp-production"
  | "fp-dr"
  | "ncdex-local"
  | "ncdex-sandbox"
  | "ncdex-production"
  | "ncdex-dr";

type AppConfig = {
  environmentGroup: EnvironmentGroup;
  auth: {
    realmUrl: string;
    client_id: string;
  };
  api: {
    api_base_url: string;
    app_backend_base_url: string;
  };
  app: {
    verifications_enabled_tenants: string[];
    v2_payments_supported_tenants: string[];
    manual_bank_account_verification_enabled_tenants: string[];
    partner_credentials_enabled_tenants: string[];
    poa_tenants: string[];
    ondc_transactions_enabled_tenants: string[];
    redirection_poa_tenant: {
      default: string;
      ondcpp?: string;
    };
    is_old_dashboard_link_enabled: boolean;
    is_thirdeye_link_enabled: boolean;
    is_scheduled_on_timestamp_enabled: boolean;
    is_product_guide_link_enabled: boolean;
    is_api_reference_link_enabled: boolean;
    is_membership_console_link_enabled: boolean;
    is_bank_statement_addition_enabled: boolean;
    is_euin_update_allowed: boolean;
    is_fatca_file_type_split_enabled: boolean;
    is_elog_generation: boolean;
    is_pan_verification_filters_in_memory_enabled: boolean;
    is_events_enabled: boolean;
    is_monitoring_alerts_enabled: boolean;
    is_aging_orders_enabled: boolean;
    is_network_intelligence_enabled: boolean;
    are_feed_submission_pages_allowed: boolean;
    is_kyc_forms_pages_enabled: boolean;
    is_generate_cams_txt_order_feed_enabled: boolean;
    is_generate_cams_dbf_order_feed_enabled: boolean;
    is_generate_kfin_txt_order_feed_enabled: boolean;
    is_generate_kfin_dbf_order_feed_enabled: boolean;
    is_payments_upstream_status_fetch_enabled: boolean;
    is_karvy_txt_sip_cancellation_report_enabled: boolean;
    order_settings: {
      mf_folio_input_detail_enabled_gateway: "ondc" | "ncdex" | null;
      is_rta_submission_post_settlement_enabled: boolean;
      is_karvy_elog_file_auto_generation_enabled: boolean;
      is_karvy_elog_file_auto_submission_enabled: boolean;
      is_karvy_sftp_credentials_enabled: boolean;
      is_force_generate_folio_imprint_enabled: boolean;
    };
    membership_portal_url: string;
    name_spaced_payments_api_enabled_tenants: string[];
    wiam_clients: string[];
  };
};

const envAppConfigMap: Record<Environment, AppConfig> = {
  "fp-local": {
    environmentGroup: "fp",
    auth: {
      realmUrl: "https://s.finprim.com/auth/realms/fp-apps",
      client_id: "fp-apps-test",
    },
    api: {
      api_base_url: "https://s.finprim.com",
      app_backend_base_url: "http://localhost:3001/dashboard",
    },
    app: {
      verifications_enabled_tenants: ["cybrillarta", "cybrillartaondcpp"],
      v2_payments_supported_tenants: ["cybrillarta", "cybrillartaondcpp"],
      manual_bank_account_verification_enabled_tenants: ["cybrillarta", "cybrillartaondcpp"],
      partner_credentials_enabled_tenants: ["cybrillarta", "cybrillartaondcpp"],
      poa_tenants: ["cybrillarta", "cybrillartaondcpp"],
      ondc_transactions_enabled_tenants: ["cybrillarta", "cybrillartaondcpp"],
      redirection_poa_tenant: {
        default: "cybrillarta",
        ondcpp: "cybrillartaondcpp",
      },
      is_old_dashboard_link_enabled: true,
      is_thirdeye_link_enabled: true,
      is_scheduled_on_timestamp_enabled: true,
      is_product_guide_link_enabled: true,
      is_api_reference_link_enabled: true,
      is_membership_console_link_enabled: false,
      is_bank_statement_addition_enabled: true,
      is_euin_update_allowed: true,
      is_fatca_file_type_split_enabled: false,
      is_elog_generation: true,
      is_pan_verification_filters_in_memory_enabled: false,
      is_events_enabled: false,
      is_monitoring_alerts_enabled: true,
      is_aging_orders_enabled: true,
      is_network_intelligence_enabled: true,
      are_feed_submission_pages_allowed: true,
      is_kyc_forms_pages_enabled: true,
      is_generate_cams_txt_order_feed_enabled: true,
      is_generate_cams_dbf_order_feed_enabled: true,
      is_generate_kfin_txt_order_feed_enabled: true,
      is_generate_kfin_dbf_order_feed_enabled: true,
      is_payments_upstream_status_fetch_enabled: false,
      is_karvy_txt_sip_cancellation_report_enabled: false,
      order_settings: {
        mf_folio_input_detail_enabled_gateway: "ondc",
        is_rta_submission_post_settlement_enabled: false,
        is_karvy_elog_file_auto_generation_enabled: false,
        is_karvy_elog_file_auto_submission_enabled: false,
        is_karvy_sftp_credentials_enabled: false,
        is_force_generate_folio_imprint_enabled: true,
      },
      membership_portal_url: "",
      name_spaced_payments_api_enabled_tenants: [],
      wiam_clients: ["fp-apps-test", "fp-ops-portal-test", "thirdeye"],
    },
  },
  "fp-staging": {
    environmentGroup: "fp",
    auth: {
      realmUrl: "https://d.finprim.com/auth/realms/fp-apps",
      client_id: "fp-apps-test",
    },
    api: {
      api_base_url: "https://d.finprim.com",
      app_backend_base_url: "https://d.finprim.com/dashboard",
    },
    app: {
      verifications_enabled_tenants: ["cybrillarta", "cybrillartaondcpp"],
      v2_payments_supported_tenants: ["cybrillarta", "cybrillartaondcpp"],
      manual_bank_account_verification_enabled_tenants: ["cybrillarta", "cybrillartaondcpp"],
      partner_credentials_enabled_tenants: ["cybrillarta", "cybrillartaondcpp"],
      poa_tenants: ["cybrillarta", "cybrillartaondcpp"],
      ondc_transactions_enabled_tenants: ["cybrillarta", "cybrillartaondcpp"],
      redirection_poa_tenant: {
        default: "cybrillarta",
        ondcpp: "cybrillartaondcpp",
      },
      is_old_dashboard_link_enabled: true,
      is_thirdeye_link_enabled: true,
      is_scheduled_on_timestamp_enabled: true,
      is_product_guide_link_enabled: true,
      is_api_reference_link_enabled: true,
      is_membership_console_link_enabled: false,
      is_bank_statement_addition_enabled: true,
      is_euin_update_allowed: true,
      is_fatca_file_type_split_enabled: false,
      is_elog_generation: true,
      is_pan_verification_filters_in_memory_enabled: false,
      is_events_enabled: false,
      is_monitoring_alerts_enabled: true,
      is_aging_orders_enabled: true,
      is_network_intelligence_enabled: true,
      are_feed_submission_pages_allowed: true,
      is_kyc_forms_pages_enabled: true,
      is_generate_cams_txt_order_feed_enabled: true,
      is_generate_cams_dbf_order_feed_enabled: true,
      is_generate_kfin_txt_order_feed_enabled: true,
      is_generate_kfin_dbf_order_feed_enabled: true,
      is_payments_upstream_status_fetch_enabled: false,
      is_karvy_txt_sip_cancellation_report_enabled: false,
      order_settings: {
        mf_folio_input_detail_enabled_gateway: "ondc",
        is_rta_submission_post_settlement_enabled: false,
        is_karvy_elog_file_auto_generation_enabled: false,
        is_karvy_elog_file_auto_submission_enabled: false,
        is_karvy_sftp_credentials_enabled: false,
        is_force_generate_folio_imprint_enabled: true,
      },
      membership_portal_url: "",
      name_spaced_payments_api_enabled_tenants: [],
      wiam_clients: ["fp-apps-test", "fp-ops-portal-test", "thirdeye"],
    },
  },
  "fp-sandbox": {
    environmentGroup: "fp",
    auth: {
      realmUrl: "https://s.finprim.com/auth/realms/fp-apps",
      client_id: "fp-apps-test",
    },
    api: {
      api_base_url: "https://s.finprim.com",
      app_backend_base_url: "https://s.finprim.com/dashboard",
    },
    app: {
      verifications_enabled_tenants: ["cybrillarta", "cybrillartaondcpp"],
      v2_payments_supported_tenants: ["cybrillarta", "cybrillartaondcpp"],
      manual_bank_account_verification_enabled_tenants: ["cybrillarta", "cybrillartaondcpp"],
      partner_credentials_enabled_tenants: ["cybrillarta", "cybrillartaondcpp"],
      poa_tenants: ["cybrillarta", "cybrillartaondcpp"],
      ondc_transactions_enabled_tenants: ["cybrillarta", "cybrillartaondcpp"],
      redirection_poa_tenant: {
        default: "cybrillarta",
        ondcpp: "cybrillartaondcpp",
      },
      is_old_dashboard_link_enabled: true,
      is_thirdeye_link_enabled: true,
      is_scheduled_on_timestamp_enabled: true,
      is_product_guide_link_enabled: true,
      is_api_reference_link_enabled: true,
      is_membership_console_link_enabled: false,
      is_bank_statement_addition_enabled: true,
      is_euin_update_allowed: true,
      is_fatca_file_type_split_enabled: false,
      is_elog_generation: true,
      is_pan_verification_filters_in_memory_enabled: false,
      is_events_enabled: false,
      is_monitoring_alerts_enabled: true,
      is_aging_orders_enabled: true,
      is_network_intelligence_enabled: true,
      are_feed_submission_pages_allowed: true,
      is_kyc_forms_pages_enabled: true,
      is_generate_cams_txt_order_feed_enabled: true,
      is_generate_cams_dbf_order_feed_enabled: true,
      is_generate_kfin_txt_order_feed_enabled: true,
      is_generate_kfin_dbf_order_feed_enabled: true,
      is_payments_upstream_status_fetch_enabled: false,
      is_karvy_txt_sip_cancellation_report_enabled: false,
      order_settings: {
        mf_folio_input_detail_enabled_gateway: "ondc",
        is_rta_submission_post_settlement_enabled: false,
        is_karvy_elog_file_auto_generation_enabled: false,
        is_karvy_elog_file_auto_submission_enabled: false,
        is_karvy_sftp_credentials_enabled: false,
        is_force_generate_folio_imprint_enabled: true,
      },
      membership_portal_url: "",
      name_spaced_payments_api_enabled_tenants: [],
      wiam_clients: ["fp-apps-test", "fp-ops-portal-test", "thirdeye"],
    },
  },
  "fp-production": {
    environmentGroup: "fp",
    auth: {
      realmUrl: "https://api.fintechprimitives.com/auth/realms/fp-apps",
      client_id: "fp-apps-live",
    },
    api: {
      api_base_url: "https://api.fintechprimitives.com",
      app_backend_base_url: "https://api.fintechprimitives.com/dashboard",
    },
    app: {
      verifications_enabled_tenants: ["cybrillapoa"],
      v2_payments_supported_tenants: ["cybrillapoa"],
      manual_bank_account_verification_enabled_tenants: ["cybrillapoa"],
      partner_credentials_enabled_tenants: ["cybrillapoa"],
      poa_tenants: ["cybrillapoa"],
      ondc_transactions_enabled_tenants: ["cybrillapoa"],
      redirection_poa_tenant: {
        default: "cybrillapoa",
      },
      is_old_dashboard_link_enabled: true,
      is_thirdeye_link_enabled: true,
      is_scheduled_on_timestamp_enabled: true,
      is_product_guide_link_enabled: true,
      is_api_reference_link_enabled: true,
      is_membership_console_link_enabled: false,
      is_bank_statement_addition_enabled: false,
      is_euin_update_allowed: true,
      is_fatca_file_type_split_enabled: false,
      is_elog_generation: true,
      is_pan_verification_filters_in_memory_enabled: false,
      is_events_enabled: false,
      is_monitoring_alerts_enabled: true,
      is_aging_orders_enabled: true,
      is_network_intelligence_enabled: true,
      are_feed_submission_pages_allowed: true,
      is_kyc_forms_pages_enabled: true,
      is_generate_cams_txt_order_feed_enabled: true,
      is_generate_cams_dbf_order_feed_enabled: true,
      is_generate_kfin_txt_order_feed_enabled: true,
      is_generate_kfin_dbf_order_feed_enabled: true,
      is_payments_upstream_status_fetch_enabled: false,
      is_karvy_txt_sip_cancellation_report_enabled: false,
      order_settings: {
        mf_folio_input_detail_enabled_gateway: "ondc",
        is_rta_submission_post_settlement_enabled: false,
        is_karvy_elog_file_auto_generation_enabled: false,
        is_karvy_elog_file_auto_submission_enabled: false,
        is_karvy_sftp_credentials_enabled: false,
        is_force_generate_folio_imprint_enabled: true,
      },
      membership_portal_url: "",
      name_spaced_payments_api_enabled_tenants: [],
      wiam_clients: ["fp-apps-live", "fp-ops-portal-live", "thirdeye"],
    },
  },
  "fp-dr": {
    environmentGroup: "fp",
    auth: {
      realmUrl: "https://api-dr.fintechprimitives.com/auth/realms/fp-apps",
      client_id: "fp-apps-live",
    },
    api: {
      api_base_url: "https://api-dr.fintechprimitives.com",
      app_backend_base_url: "https://api-dr.fintechprimitives.com/dashboard",
    },
    app: {
      verifications_enabled_tenants: ["cybrillapoa"],
      v2_payments_supported_tenants: ["cybrillapoa"],
      manual_bank_account_verification_enabled_tenants: ["cybrillapoa"],
      partner_credentials_enabled_tenants: ["cybrillapoa"],
      poa_tenants: ["cybrillapoa"],
      ondc_transactions_enabled_tenants: ["cybrillapoa"],
      redirection_poa_tenant: {
        default: "cybrillapoa",
      },
      is_old_dashboard_link_enabled: true,
      is_thirdeye_link_enabled: true,
      is_scheduled_on_timestamp_enabled: true,
      is_product_guide_link_enabled: true,
      is_api_reference_link_enabled: true,
      is_membership_console_link_enabled: false,
      is_bank_statement_addition_enabled: false,
      is_euin_update_allowed: true,
      is_fatca_file_type_split_enabled: false,
      is_elog_generation: true,
      is_pan_verification_filters_in_memory_enabled: false,
      is_events_enabled: false,
      is_monitoring_alerts_enabled: true,
      is_aging_orders_enabled: true,
      is_network_intelligence_enabled: true,
      are_feed_submission_pages_allowed: true,
      is_kyc_forms_pages_enabled: true,
      is_generate_cams_txt_order_feed_enabled: true,
      is_generate_cams_dbf_order_feed_enabled: true,
      is_generate_kfin_txt_order_feed_enabled: true,
      is_generate_kfin_dbf_order_feed_enabled: true,
      is_payments_upstream_status_fetch_enabled: false,
      is_karvy_txt_sip_cancellation_report_enabled: false,
      order_settings: {
        mf_folio_input_detail_enabled_gateway: "ondc",
        is_rta_submission_post_settlement_enabled: false,
        is_karvy_elog_file_auto_generation_enabled: false,
        is_karvy_elog_file_auto_submission_enabled: false,
        is_karvy_sftp_credentials_enabled: false,
        is_force_generate_folio_imprint_enabled: true,
      },
      membership_portal_url: "",
      name_spaced_payments_api_enabled_tenants: [],
      wiam_clients: ["fp-apps-live", "fp-ops-portal-live", "thirdeye"],
    },
  },
  "ncdex-local": {
    environmentGroup: "ncdex",
    auth: {
      realmUrl: "https://api.ncdexnidhi-sandbox.com/auth/realms/fp-apps",
      client_id: "fp-apps-test",
    },
    api: {
      api_base_url: "https://api.ncdexnidhi-sandbox.com",
      app_backend_base_url: "http://localhost:3001/dashboard",
    },
    app: {
      verifications_enabled_tenants: ["ncdex"],
      v2_payments_supported_tenants: ["ncnccl", "nccl"],
      manual_bank_account_verification_enabled_tenants: ["ncdex"],
      partner_credentials_enabled_tenants: ["ncdex"],
      poa_tenants: ["ncdex"],
      ondc_transactions_enabled_tenants: ["ncdex"],
      redirection_poa_tenant: {
        default: "nccl",
      },
      is_old_dashboard_link_enabled: false,
      is_thirdeye_link_enabled: false,
      is_scheduled_on_timestamp_enabled: false,
      is_product_guide_link_enabled: false,
      is_api_reference_link_enabled: false,
      is_membership_console_link_enabled: true,
      is_bank_statement_addition_enabled: true,
      is_euin_update_allowed: false,
      is_fatca_file_type_split_enabled: true,
      is_elog_generation: true,
      is_pan_verification_filters_in_memory_enabled: true,
      is_events_enabled: false,
      is_monitoring_alerts_enabled: false,
      is_aging_orders_enabled: true,
      is_network_intelligence_enabled: false,
      are_feed_submission_pages_allowed: true,
      is_kyc_forms_pages_enabled: false,
      is_generate_cams_txt_order_feed_enabled: false,
      is_generate_cams_dbf_order_feed_enabled: true,
      is_generate_kfin_txt_order_feed_enabled: true,
      is_generate_kfin_dbf_order_feed_enabled: false,
      is_payments_upstream_status_fetch_enabled: false,
      is_karvy_txt_sip_cancellation_report_enabled: true,
      order_settings: {
        mf_folio_input_detail_enabled_gateway: "ncdex",
        is_rta_submission_post_settlement_enabled: true,
        is_karvy_elog_file_auto_generation_enabled: true,
        is_karvy_elog_file_auto_submission_enabled: true,
        is_karvy_sftp_credentials_enabled: false,
        is_force_generate_folio_imprint_enabled: false,
      },
      membership_portal_url: "https://membership.ncdexnidhi-sandbox.com/admin",
      name_spaced_payments_api_enabled_tenants: ["nccl"],
      wiam_clients: ["fp-apps-test"],
    },
  },
  "ncdex-sandbox": {
    environmentGroup: "ncdex",
    auth: {
      realmUrl: "https://api.ncdexnidhi-sandbox.com/auth/realms/fp-apps",
      client_id: "fp-apps-test",
    },
    api: {
      api_base_url: "https://api.ncdexnidhi-sandbox.com",
      app_backend_base_url: "https://api.ncdexnidhi-sandbox.com/dashboard",
    },
    app: {
      verifications_enabled_tenants: ["ncdex"],
      v2_payments_supported_tenants: ["ncnccl", "nccl"],
      manual_bank_account_verification_enabled_tenants: ["ncdex"],
      partner_credentials_enabled_tenants: ["ncdex"],
      poa_tenants: ["ncdex"],
      ondc_transactions_enabled_tenants: ["ncdex"],
      redirection_poa_tenant: {
        default: "nccl",
      },
      is_old_dashboard_link_enabled: false,
      is_thirdeye_link_enabled: false,
      is_scheduled_on_timestamp_enabled: false,
      is_product_guide_link_enabled: false,
      is_api_reference_link_enabled: false,
      is_membership_console_link_enabled: true,
      is_bank_statement_addition_enabled: true,
      is_euin_update_allowed: false,
      is_fatca_file_type_split_enabled: true,
      is_elog_generation: true,
      is_pan_verification_filters_in_memory_enabled: true,
      is_events_enabled: false,
      is_monitoring_alerts_enabled: false,
      is_aging_orders_enabled: true,
      is_network_intelligence_enabled: false,
      are_feed_submission_pages_allowed: true,
      is_kyc_forms_pages_enabled: false,
      is_generate_cams_txt_order_feed_enabled: false,
      is_generate_cams_dbf_order_feed_enabled: true,
      is_generate_kfin_txt_order_feed_enabled: true,
      is_generate_kfin_dbf_order_feed_enabled: false,
      is_payments_upstream_status_fetch_enabled: false,
      is_karvy_txt_sip_cancellation_report_enabled: true,
      order_settings: {
        mf_folio_input_detail_enabled_gateway: "ncdex",
        is_rta_submission_post_settlement_enabled: true,
        is_karvy_elog_file_auto_generation_enabled: true,
        is_karvy_elog_file_auto_submission_enabled: true,
        is_karvy_sftp_credentials_enabled: false,
        is_force_generate_folio_imprint_enabled: false,
      },
      membership_portal_url: "https://membership.ncdexnidhi-sandbox.com/admin",
      name_spaced_payments_api_enabled_tenants: ["nccl"],
      wiam_clients: ["fp-apps-test"],
    },
  },
  "ncdex-production": {
    environmentGroup: "ncdex",
    auth: {
      realmUrl: "https://api.ncdexnidhi.com/auth/realms/fp-apps",
      client_id: "fp-apps-live",
    },
    api: {
      api_base_url: "https://api.ncdexnidhi.com",
      app_backend_base_url: "https://api.ncdexnidhi.com/dashboard",
    },
    app: {
      verifications_enabled_tenants: ["ncdex"],
      v2_payments_supported_tenants: ["ncnccl", "nccl"],
      manual_bank_account_verification_enabled_tenants: ["ncdex"],
      partner_credentials_enabled_tenants: ["ncdex"],
      poa_tenants: ["ncdex"],
      ondc_transactions_enabled_tenants: ["ncdex"],
      redirection_poa_tenant: {
        default: "nccl",
      },
      is_old_dashboard_link_enabled: false,
      is_thirdeye_link_enabled: false,
      is_scheduled_on_timestamp_enabled: false,
      is_product_guide_link_enabled: false,
      is_api_reference_link_enabled: false,
      is_membership_console_link_enabled: true,
      is_bank_statement_addition_enabled: false,
      is_euin_update_allowed: false,
      is_fatca_file_type_split_enabled: true,
      is_elog_generation: true,
      is_pan_verification_filters_in_memory_enabled: true,
      is_events_enabled: false,
      is_monitoring_alerts_enabled: false,
      is_aging_orders_enabled: true,
      is_network_intelligence_enabled: false,
      are_feed_submission_pages_allowed: true,
      is_kyc_forms_pages_enabled: false,
      is_generate_cams_txt_order_feed_enabled: false,
      is_generate_cams_dbf_order_feed_enabled: true,
      is_generate_kfin_txt_order_feed_enabled: true,
      is_generate_kfin_dbf_order_feed_enabled: false,
      is_payments_upstream_status_fetch_enabled: false,
      is_karvy_txt_sip_cancellation_report_enabled: true,
      order_settings: {
        mf_folio_input_detail_enabled_gateway: "ncdex",
        is_rta_submission_post_settlement_enabled: true,
        is_karvy_elog_file_auto_generation_enabled: true,
        is_karvy_elog_file_auto_submission_enabled: true,
        is_karvy_sftp_credentials_enabled: false,
        is_force_generate_folio_imprint_enabled: false,
      },
      membership_portal_url: "https://membership.ncdexnidhi.com/admin",
      name_spaced_payments_api_enabled_tenants: ["nccl"],
      wiam_clients: ["fp-apps-live"],
    },
  },
  "ncdex-dr": {
    environmentGroup: "ncdex",
    auth: {
      realmUrl: "https://api-dr.ncdexnidhi.com/auth/realms/fp-apps",
      client_id: "fp-apps-live",
    },
    api: {
      api_base_url: "https://api-dr.ncdexnidhi.com",
      app_backend_base_url: "https://api-dr.ncdexnidhi.com/dashboard",
    },
    app: {
      verifications_enabled_tenants: ["ncdex"],
      v2_payments_supported_tenants: ["ncnccl", "nccl"],
      manual_bank_account_verification_enabled_tenants: ["ncdex"],
      partner_credentials_enabled_tenants: ["ncdex"],
      poa_tenants: ["ncdex"],
      ondc_transactions_enabled_tenants: ["ncdex"],
      redirection_poa_tenant: {
        default: "nccl",
      },
      is_old_dashboard_link_enabled: false,
      is_thirdeye_link_enabled: false,
      is_scheduled_on_timestamp_enabled: false,
      is_product_guide_link_enabled: false,
      is_api_reference_link_enabled: false,
      is_membership_console_link_enabled: true,
      is_bank_statement_addition_enabled: false,
      is_euin_update_allowed: false,
      is_fatca_file_type_split_enabled: true,
      is_elog_generation: true,
      is_pan_verification_filters_in_memory_enabled: true,
      is_karvy_txt_sip_cancellation_report_enabled: true,
      order_settings: {
        mf_folio_input_detail_enabled_gateway: "ncdex",
        is_rta_submission_post_settlement_enabled: true,
        is_karvy_elog_file_auto_generation_enabled: true,
        is_karvy_elog_file_auto_submission_enabled: true,
        is_karvy_sftp_credentials_enabled: false,
        is_force_generate_folio_imprint_enabled: false,
      },
      is_events_enabled: false,
      is_monitoring_alerts_enabled: false,
      is_aging_orders_enabled: true,
      is_network_intelligence_enabled: false,
      are_feed_submission_pages_allowed: true,
      is_kyc_forms_pages_enabled: false,
      is_generate_cams_txt_order_feed_enabled: false,
      is_generate_cams_dbf_order_feed_enabled: true,
      is_generate_kfin_txt_order_feed_enabled: true,
      is_generate_kfin_dbf_order_feed_enabled: false,
      is_payments_upstream_status_fetch_enabled: true,
      membership_portal_url: "https://membership-dr.ncdexnidhi.com/admin",
      name_spaced_payments_api_enabled_tenants: ["nccl"],
      wiam_clients: ["fp-apps-live"],
    },
  },
};

const platformIdentifier = import.meta.env.VITE_PLATFORM_IDENTIFIER;

const appConfig = envAppConfigMap[platformIdentifier as Environment];

export default appConfig;
